import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import type shaka from 'shaka-player/dist/shaka-player.ui';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { logError } from '@jwp/ott-common/src/logger';
import { useMediaSources } from '@jwp/ott-hooks-react/src/useMediaSources';
import env from '@jwp/ott-common/src/env';
import { MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';

import { type PlayerProps } from '../Player/Player';
import useStableHandlers from '../../hooks/useStableHandlers';

import { isHlsType, isMpegDash, supportsFairPlay } from './utils';
import styles from './PlayerShaka.module.scss';
import fontUrl from './material-icons.otf';

let cachedFont: FontFace | null = null;
const loadFont = async (): Promise<FontFace | undefined> => {
  if (cachedFont) {
    return cachedFont;
  }

  try {
    const font = new FontFace('Material Icons Round', `url(${fontUrl}) format('opentype')`, {
      style: 'normal',
      weight: '400',
    });

    await font.load();
    document.fonts.add(font);
    cachedFont = font;

    return font;
  } catch (error) {
    logError('PlayerShaka', 'error loading the font', { error });
    return undefined;
  }
};

const PlayerShaka = ({ className, controls = true, on = {}, item, adsData, autostart, startTime, playerHandle, onPlaylistItemCallback }: PlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoAdContainerRef = useRef<HTMLDivElement>(null);
  const currentAd = useRef<shaka.ads.InterstitialAd | null>(null);
  const isFirstFrame = useRef(true);
  const [player, setPlayer] = useState<shaka.Player | null>(null);
  const [ui, setUi] = useState<shaka.ui.Overlay | null>(null);
  const sources = useMediaSources({ item, baseUrl: env.APP_CONTENT_API_BASE_URL });
  const stableOn = useStableHandlers(on);

  const cleanupPlayer = useEventCallback(() => {
    if (ui) ui.destroy();
    if (player) player.destroy();
    stableOn.remove?.({ type: 'remove' });
  });

  const loadVideo = useEventCallback(async (item: PlaylistItem) => {
    if (!player) return;

    const callbackResult = await onPlaylistItemCallback?.({ ...item, sources });
    const playlistItem = callbackResult || item;

    const hlsSource = playlistItem.sources.find((source) => isHlsType(source.type));
    const dashSource = playlistItem.sources.find((source) => isMpegDash(source.type));
    let videoSource = dashSource || hlsSource;

    if (hlsSource && (await supportsFairPlay())) {
      videoSource = hlsSource;
    }

    if (videoSource?.drm) {
      const playReadyLicenseServer = videoSource?.drm?.playready?.url;
      const widevineLicenseServer = videoSource?.drm?.widevine?.url;
      const fairplayCertificateUrl = videoSource?.drm?.fairplay?.certificateUrl;
      const fairplayProcessSpcUrl = videoSource?.drm?.fairplay?.processSpcUrl;

      player.configure({
        drm: {
          servers: {
            'com.widevine.alpha': widevineLicenseServer,
            'com.microsoft.playready': playReadyLicenseServer,
            'com.apple.fps': fairplayProcessSpcUrl,
          },
          advanced: {
            'com.apple.fps': {
              serverCertificateUri: fairplayCertificateUrl,
            },
          },
        },
      });
    }

    try {
      // setup ad client
      const isLiveMedia = item['VCH.EventState'] !== undefined || item.scheduledStart !== undefined || item.mediaStatus === MediaStatus.LIVE;
      if (adsData?.client === 'vast' && !isLiveMedia) {
        const adManager = player.getAdManager();
        const scheduleUrl = Array.isArray(adsData.schedule) ? adsData.schedule[0]?.tag?.[0] : adsData.schedule;

        if (scheduleUrl) {
          adManager?.initInterstitial(videoAdContainerRef.current!, player, videoRef.current!);
          adManager?.addAdUrlInterstitial(scheduleUrl);
        }
      }

      // load video
      if (videoSource?.file) {
        const mimeType = isHlsType(videoSource.type) ? 'application/vnd.apple.mpegurl' : videoSource.type;
        await player.load(videoSource.file, startTime === 0 ? undefined : startTime, mimeType);
      }

      // load external text tracks
      playlistItem.tracks
        ?.filter((track) => track.kind === 'captions' && !track.includedInManifest)
        .forEach((track) => {
          player.addTextTrackAsync(track.file, track.language || 'en', 'subtitles');
        });

      stableOn.playlistItem?.({ type: 'playlistItem' });
    } catch (error) {
      logError('PlayerShaka', 'error loading the video', { error });
    }
  });

  useImperativeHandle(
    playerHandle,
    () => {
      return {
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        seek: (position) => {
          if (videoRef.current) videoRef.current.currentTime = position;
        },
        getPosition: async () => videoRef.current?.currentTime || 0,
        getDuration: async () => videoRef.current?.duration || 0,
        getState: async () => {
          if (!videoRef.current?.played.length) return 'idle';
          return videoRef.current?.paused ? 'paused' : 'playing';
        },
        setCurrentAudioTrack: async (track: number) => {
          const audioTracks = player?.getAudioTracks() || [];
          const selectedAudioTrack = audioTracks[track];

          player?.configure('preferredAudioLanguage', selectedAudioTrack?.language);
          // setAudioLanguage has a bug https://github.com/shaka-project/shaka-player/pull/8857
          player?.selectAudioLanguage(selectedAudioTrack?.language);
          // player?.selectAudioTrack(selectedAudioTrack);
        },
        setCurrentCaptionTrack: async (track: number) => {
          const tracks = player?.getTextTracks() || [];
          if (track > 0) {
            // The first track `0` is the manually added "disabled", so we need to compensate for that
            player?.selectTextTrack(tracks[track - 1]);
            player?.setTextTrackVisibility(true);
            player?.configure('preferredTextLanguage', tracks[track - 1]?.language);
          } else {
            player?.setTextTrackVisibility(false);
          }
        },
        skipAd: async () => {
          if (currentAd.current?.canSkipNow()) currentAd.current?.skip();
        },
      };
    },
    [player],
  );

  useEffect(() => {
    // load library
    async function load() {
      const shaka = (await import('shaka-player/dist/shaka-player.ui')).default;

      if (containerRef.current && videoRef.current) {
        const player = new shaka.Player();

        player.configure({ streaming: { preferNativeHls: false } });

        if (controls) {
          await loadFont();
          await import('./shaka-controls.css');
          const ui = new shaka.ui.Overlay(player, containerRef.current, videoRef.current);

          const config = {
            controlPanelElements: [
              'play_pause',
              'mute',
              'volume',
              'time_and_duration',
              'spacer',
              'cast',
              'remote' in HTMLMediaElement.prototype ? 'remote' : 'WebKitPlaybackTargetAvailabilityEvent' in window ? 'airplay' : undefined,
              'language',
              'captions',
              'overflow_menu',
            ],
            overflowMenuButtons: ['quality', 'picture_in_picture'],
            forceLandscapeOnFullscreen: false,
            fadeDelay: 1,
          };
          ui.configure(config);

          ui.getControls()?.addEventListener('showingui', () => stableOn.userActive?.({ type: 'userActive' }));
          ui.getControls()?.addEventListener('hidingui', () => stableOn.userInactive?.({ type: 'userInactive' }));
          setUi(ui);
        }

        // add event listeners
        const handleTextTracksChanged = () => {
          const nativeTracks = player.getTextTracks();
          const isVisible = player.isTextTrackVisible();
          const tracks = [{ label: 'Disabled', language: '', id: 0 }, ...nativeTracks].map((track, index) => ({
            index,
            label: track.label || track.language,
            id: track.id,
          }));
          // compensate for the "disabled" track that we've added manually
          const currentTrack = isVisible ? nativeTracks.findIndex((track) => track.active) + 1 : 0;

          stableOn.captions?.({ type: 'captions', tracks, currentTrack });
        };

        player.addEventListener('textchanged', handleTextTracksChanged);
        player.addEventListener('trackschanged', handleTextTracksChanged);

        const handleAudioTrackEvent = () => {
          const nativeTracks = player.getAudioTracks();
          const tracks = nativeTracks.map((track, index) => ({
            label: track.label || track.language,
            name: track.language,
            index: index,
          }));
          const currentTrack = nativeTracks.findIndex((track) => track.active);

          stableOn?.audioTracks?.({ type: 'audioTracks', tracks, currentTrack });
        };

        player.addEventListener('audiotrackchanged', handleAudioTrackEvent);
        player.addEventListener('audiotrackschanged', handleAudioTrackEvent);

        // Setup ad events
        const adManager = player.getAdManager();
        const skipAdButton = document.querySelector('.shaka-skip-ad-button');
        if (adManager) {
          // Some iOS devices ignore the first click; this forces it to work
          skipAdButton?.addEventListener('touchstart', () => {
            if (currentAd.current?.canSkipNow()) currentAd.current?.skip();
          });
          adManager.addEventListener(shaka.ads.Utils.AD_STARTED, (e) => {
            // @ts-expect-error event doesn't contain a typed ad
            currentAd.current = e.ad as shaka.ads.InterstitialAd;

            stableOn.adPlay?.({
              type: 'adPlay',
              item: currentAd.current?.getPositionInSequence() || 1,
              total: currentAd.current?.getSequenceLength() || 1,
              skipOffset: currentAd.current?.getTimeUntilSkippable() + currentAd.current?.getTimeOffset() || -1,
            });
          });
          adManager.addEventListener(shaka.ads.Utils.AD_IMPRESSION, () => {
            stableOn.adImpression?.({ type: 'adImpression' });
          });
          adManager.addEventListener(shaka.ads.Utils.AD_SKIPPED, () => {
            stableOn.adSkipped?.({ type: 'adSkipped' });
            currentAd.current = null;
          });
          adManager.addEventListener(shaka.ads.Utils.AD_COMPLETE, () => {
            stableOn.adComplete?.({ type: 'adComplete' });
            currentAd.current = null;
          });
          adManager.addEventListener(shaka.ads.Utils.AD_PAUSED, () => {
            stableOn.adPause?.({ type: 'adPause' });
          });
          adManager.addEventListener(shaka.ads.Utils.AD_RESUMED, () => {
            stableOn.adPlay?.({
              type: 'adPlay',
              item: currentAd.current?.getPositionInSequence() || 1,
              total: currentAd.current?.getSequenceLength() || 1,
              skipOffset: currentAd.current?.getTimeUntilSkippable() + currentAd.current?.getTimeOffset() || -1,
            });
          });
          adManager.addEventListener(shaka.ads.Utils.AD_CLOSED, () => {
            currentAd.current = null;
          });
          adManager.addEventListener(shaka.ads.Utils.AD_ERROR, () => {
            currentAd.current = null;
            stableOn.adError?.({ type: 'adError' });
          });
        }

        await player.attach(videoRef.current);
        setPlayer(player);
        stableOn.ready?.({ type: 'ready' });
      }
    }

    load();

    return cleanupPlayer;
  }, [cleanupPlayer, controls, stableOn]);

  useEffect(() => {
    videoRef.current?.addEventListener('play', () => {
      if (isFirstFrame.current) stableOn.firstFrame?.({ type: 'firstFrame' });
      stableOn.beforePlay?.({ type: 'beforePlay' });
      stableOn.play?.({ type: 'play' });
    });

    videoRef.current?.addEventListener('pause', () => stableOn.pause?.({ type: 'pause' }));
    videoRef.current?.addEventListener('ended', () => stableOn.complete?.({ type: 'complete' }));
    videoRef.current?.addEventListener('timeupdate', () => {
      stableOn.time?.({
        type: 'time',
        position: videoRef.current?.currentTime || 0,
        duration: videoRef.current?.duration || 0,
      });
    });
    videoRef.current?.addEventListener('seeking', () => {
      stableOn.seek?.({
        type: 'seek',
        position: videoRef.current?.currentTime || 0,
        duration: videoRef.current?.duration || 0,
        offset: 0,
      });
    });
    videoRef.current?.addEventListener('seeked', () => stableOn.seeked?.({ type: 'seeked' }));
  }, [stableOn]);

  useEffect(() => {
    if (item && player) loadVideo(item);
  }, [player, item, loadVideo]);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-shaka-player-cast-receiver-id={env.APP_CHROMECAST_APPLICATION_ID}
      data-shaka-player-cast-android-receiver-compatible={env.APP_CHROMECAST_APPLICATION_ID ? 'true' : 'false'}
    >
      <video ref={videoRef} className={styles.video} poster={item.backgroundImage} autoPlay={autostart} playsInline={true} />
      <div ref={videoAdContainerRef} className={styles.ad} />
    </div>
  );
};

export default PlayerShaka;
